import React from 'react';
import styled from '@emotion/styled';
import { Text } from 'ds/components/typography';
import { useLocale, ITranslate } from 'locale';
import { OfferDetails } from 'components/unit-page/main-details/styled';
import { OfferDetail } from 'components/unit-page/main-details/OfferDetail';

import UnitIcon from 'assets/svg/project/unit.svg';
import FloorIcon from 'assets/svg/floor.svg';
import TypeIcon from 'assets/svg/project/type.svg';
import { MainInfoBlock, MainInfoItem, LabelsRow, AdditionalInfoRow, ProjectTitle, ProjectSubtitle, ProjectTitleSEO, DeveloperLink, Divider, DeveloperWrapper, OfferDetailsLabel, NotPromotedLabel } from './styled';
import { IAddressDetails, IDeveloper, MarketplaceType } from 'utils/entities';
import { marketplaceSelector, routeSelector } from 'store/state/selectors/router';
import { useSelector } from 'react-redux';
import { Route } from 'config/routes';
import { SpacingBlock } from 'components/spacing-block';

const Wrapper = styled.div`
  ${({ theme }) => `
    margin-bottom: ${theme.spacing(3)};
    color: ${theme.colors.neutrals.grey1};
  `}
`;

interface Range {
  min: number;
  max: number;
}

interface ProjectMainInfoProps {
  address: IAddressDetails;
  name: string;
  minPrice: number;
  roomsRange: Range;
  isNewProject: boolean;
  type: string;
  showLabel: boolean;
  buildingsAmount: number;
  floorsRange: Range;
  apartmentsAmount?: number;
  projectLogo?: string;
  developers?: IDeveloper[];
  isPromoted: boolean;
}

export function getProjectAddress(address: IAddressDetails, t: ITranslate): string {
  const { streetName, streetNumber, city } = address;
  const unknown = t('projectPage.unknown');
  if (streetName && streetName.includes(unknown)) {
    return city;
  }
  return [ streetName, streetNumber, city ].filter(Boolean).join(' ');
}

const ProjectMainInfo: React.FC<ProjectMainInfoProps> = (props) => {
  const {
    address,
    name,
    minPrice,
    roomsRange,
    isNewProject,
    type,
    showLabel,
    buildingsAmount,
    floorsRange,
    apartmentsAmount,
    developers,
    isPromoted,
  } = props;
  const marketplace = useSelector(marketplaceSelector);
  const isCommercial = marketplace === MarketplaceType.Commercial;
  const formatRange = (range: Range) => range.min === range.max ? range.min : `${range.min}-${range.max}`;
  const { formatMoney, t } = useLocale();
  const route = useSelector(routeSelector);
  const isCommercialProject = route.name === Route.ProjectPageCommercial;

  const additionalInfo = (
    <AdditionalInfoRow data-auto="project-additional-info" isCommercial={isCommercial}>
      <OfferDetails data-auto="project-offer-details" style={{ marginBottom: 8 }}>
        {buildingsAmount ? (
          <OfferDetail icon={<TypeIcon width={16} height={16} />}>
            {t('projectPage.buildings', { count: buildingsAmount })}
          </OfferDetail>
        ) : null}

        {floorsRange && floorsRange.min ? (
          <OfferDetail icon={<FloorIcon width={16} height={16} />}>
            {t('projectPage.floorsRange', { min: floorsRange.min, max: floorsRange.min === floorsRange.max ? null : floorsRange.max })}
          </OfferDetail>
        ) : null}

        {apartmentsAmount ? (
          <OfferDetail icon={<UnitIcon width={16} height={16} />}>
            {t('projectPage.apartmentsAmount', { count: apartmentsAmount })}
          </OfferDetail>
        ) : null}
      </OfferDetails>
    </AdditionalInfoRow>
  );

  return (
    <Wrapper>
      <MainInfoBlock data-auto="project-main-info-block">
        <MainInfoItem isCommercial={isCommercial}>
          <ProjectTitleSEO data-auto="project-name">{name}</ProjectTitleSEO>
          <ProjectSubtitle data-auto="project-address">{getProjectAddress(address, t)}</ProjectSubtitle>
          {isPromoted ? null : <NotPromotedLabel>
            {t('projectPage.notPromotedLabel')}
          </NotPromotedLabel>}
        </MainInfoItem>
        {minPrice ? (
          <MainInfoItem isCommercial={isCommercial}>
            {!isCommercialProject ? (
              <ProjectSubtitle data-auto="project-rooms-range" isLeft>
                {t('projectPage.roomsRange.modeB', { count: formatRange(roomsRange) })}
              </ProjectSubtitle>
            ) : (
              <ProjectSubtitle isLeft>
                {t('projectPage.textAbovePrice')}
              </ProjectSubtitle>
            )}
            <ProjectTitle isLeft>{formatMoney(minPrice)}</ProjectTitle>
          </MainInfoItem>
        ) : null}
      </MainInfoBlock>
      {isCommercial ? null : (
        <LabelsRow isModeB data-auto="project-type">
          {isNewProject ? (
            <OfferDetailsLabel isNew={isNewProject}>
              {t('projectPage.new')}
            </OfferDetailsLabel>
          ) : null}
          {showLabel ? (
            <div>
              <OfferDetailsLabel>
                {t('projectPage.priceLabel')}
              </OfferDetailsLabel>
            </div>
          ) : null}
        </LabelsRow>
      )}
      <SpacingBlock mTop={4} mBottom={8}>
        <DeveloperWrapper>
        <Text>{t('projectPage.developerPrefix')}</Text>
          {developers.map(developer => (
            <React.Fragment key={developer.developerLink}>
              <DeveloperLink href={developer.developerLink} target="_blank" data-auto="developer-link">
                {developer.name}
              </DeveloperLink>
              <Divider>•</Divider>
            </React.Fragment>
          ))}
          <Text>{t('projectPage.projectType', { type })}</Text>
        </DeveloperWrapper>
        {additionalInfo}
      </SpacingBlock>
    </Wrapper>
  );
};

export default ProjectMainInfo;
